import { useEffect, useRef, useState } from 'react';
import toast from 'react-hot-toast';
import { Modal } from '@/components/Modal';
import { Button } from '@/components/Button';
import { useCompanyCurrency } from '@/contexts/WorkspaceContext';
import { PropertyType, PropertyUnit, UnitDetail, realEstateService } from '@/services/real-estate.service';
import {
  UnitDraft, UnitErrors, UnitFields, defaultUnitType, draftFromUnit, emptyUnitDraft, unitCreatePayload, unitPatch, validateUnitDraft,
} from './UnitFields';

interface Props {
  open: boolean;
  propertyUid: string;
  propertyType: PropertyType;
  propertyName: string;
  /** Lets the form tell a unit's own owner from the one it inherits. */
  propertyOwnerUid?: string;
  /** Edit this unit; without it the modal adds a new one to the property. */
  unit?: PropertyUnit | null;
  onClose: () => void;
  onSaved: (u: UnitDetail) => void;
}

export default function UnitFormModal({ open, propertyUid, propertyType, propertyName, propertyOwnerUid, unit, onClose, onSaved }: Props) {
  const companyCurrency = useCompanyCurrency();
  const [draft, setDraft] = useState<UnitDraft>(() => emptyUnitDraft());
  const initial = useRef<UnitDraft>(emptyUnitDraft());
  const [errors, setErrors] = useState<UnitErrors>({});
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    if (!open) return;
    const d = unit ? draftFromUnit(unit, propertyOwnerUid) : emptyUnitDraft({ unitType: defaultUnitType(propertyType) });
    initial.current = d;
    setDraft(d);
    setErrors({});
  }, [open, unit, propertyOwnerUid, propertyType]);

  const save = async () => {
    const e = validateUnitDraft(draft);
    setErrors(e);
    const first = Object.values(e)[0];
    if (first) { toast.error(first); return; }
    setBusy(true);
    try {
      if (unit) {
        const body = unitPatch(draft, initial.current);
        if (Object.keys(body).length === 0) { onClose(); return; }
        const saved = await realEstateService.updateUnit(unit.unitUid, body);
        toast.success(`${saved.label} updated`);
        onSaved(saved);
      } else {
        const saved = await realEstateService.createUnit(propertyUid, unitCreatePayload(draft));
        toast.success(`${saved.label} added to ${propertyName}`);
        onSaved(saved);
      }
    } catch (err: unknown) {
      if (!(err as { response?: unknown })?.response) toast.error('Could not save — check your connection');
    } finally {
      setBusy(false);
    }
  };

  return (
    <Modal open={open} onClose={() => !busy && onClose()} title={unit ? `Edit ${unit.label}` : `Add a unit to ${propertyName}`} widthClass="max-w-2xl">
      <UnitFields
        draft={draft}
        onChange={(patch) => { setDraft((d) => ({ ...d, ...patch })); setErrors({}); }}
        errors={errors}
        currency={unit?.currency || companyCurrency}
        leaseLocked={!!unit?.currentLeaseUid}
        autoFocus={!unit}
      />
      <div className="mt-2 flex justify-end gap-2 border-t border-border pt-4">
        <Button variant="secondary" onClick={onClose} disabled={busy}>Cancel</Button>
        <Button variant="primary" onClick={() => void save()} loading={busy}>{unit ? 'Save' : 'Add unit'}</Button>
      </div>
    </Modal>
  );
}
